import { computeProration } from "@rflocal/shared/billing/prorateCalculation";

export type TierChangeOverride = {
    renewalDate: string;
    currentCredits: number | null;
    newTierPrice: number;
    currentTierPrice: number;
    pricePerCredit: number;
    unusedCredits: number;
    cycleCreditsUsed: number;
    cycleStartSeconds: number;
    cycleEndSeconds: number;
    isAnnual: boolean;
    addOnCost: number;
};

// Frontend estimate of the prorated checkout price for a Core plan tier change.
// The authoritative calculation is on the backend in computeProrateDeduction (checkoutSession.js).
// See app/docs/PRORATE_DISCOUNTS.md for full documentation.
//
// All values are per billing cycle (monthly prices + monthly credits for monthly,
// yearly prices + yearly credits for annual).
//
// Formula:
//   proratedPrice = newTierPrice
//                 - (unusedCredits × pricePerCredit)
//                 - serviceFee × (timeRemaining / totalCycleLength)
//                 + addOnCost  (seats re-charged on new subscription)
//
// Where:
//   - pricePerCredit = from PREPAID_CREDIT_PACKS.core matched by current tier credits
//   - serviceFee     = currentTierPrice - (includedCredits × pricePerCredit) + addOnCost
//   - addOnCost      = purchased seat add-ons (collaborator limit - plan base seats) × seat price
//   - timeRemaining  = whole days remaining in billing cycle, excluding today
export function computeProratedPrice(override: TierChangeOverride): number {
    const {
        newTierPrice,
        currentTierPrice,
        pricePerCredit,
        unusedCredits,
        cycleCreditsUsed,
        cycleStartSeconds,
        cycleEndSeconds,
        addOnCost
    } = override;
    const billingPeriod = override.isAnnual ? "/yr" : "/mo";
    const totalIncludedCredits = unusedCredits + cycleCreditsUsed;

    // Use the shared proration formula (single-sourced with the backend)
    const proration = computeProration({
        unusedCredits,
        pricePerCredit,
        currentTierPrice,
        includedCredits: totalIncludedCredits,
        addOnCost,
        cycleStartSeconds,
        cycleEndSeconds
    });

    // Add seat add-on cost so the result matches the Stripe checkout total
    // (new tier prorated price + seats charged on the new subscription)
    const result = Math.max(0, newTierPrice - proration.totalDeduction) + addOnCost;
    const resultRounded = Math.round(result * 100) / 100;

    if (process.env.NODE_ENV === "development") {
        /* eslint-disable no-console */
        console.group("[TierChange] Prorated price breakdown");
        console.log("Billing period:", override.isAnnual ? "annual" : "monthly");
        console.log("New tier cost:", `$${newTierPrice}${billingPeriod}`);
        console.log(
            "Unused credit refund:",
            `${unusedCredits} unused credits${billingPeriod} × $${pricePerCredit}/credit = -$${proration.unusedCreditRefund.toFixed(2)}`,
            `(${cycleCreditsUsed} used of ${totalIncludedCredits} included${billingPeriod})`
        );
        console.log(
            "Add-on: Seats:",
            addOnCost > 0
                ? `+$${addOnCost.toFixed(2)}${billingPeriod} for seats add-ons`
                : "No seats add-ons"
        );
        console.log(
            "Service fee breakdown:",
            `$${currentTierPrice}${billingPeriod} current tier price - ${totalIncludedCredits} credits × $${pricePerCredit}/credit` +
                (addOnCost > 0 ? ` + $${addOnCost.toFixed(2)} seats add-ons` : "") +
                ` = $${proration.serviceFee.toFixed(2)} service fee`
        );
        console.log(
            "Service cost refund:",
            `$${proration.serviceFee.toFixed(2)} service fee × ${(proration.timeRemainingRatio * 100).toFixed(1)}% remaining = -$${proration.serviceFeeRefund.toFixed(2)}`
        );
        console.log(
            "Cycle info:",
            `start=${new Date(cycleStartSeconds * 1000).toLocaleDateString()},`,
            `end=${new Date(cycleEndSeconds * 1000).toLocaleDateString()},`,
            `now=${new Date().toLocaleDateString()},`,
            `total=${proration.totalCycleDays} days,`,
            `remaining=${proration.daysRemaining} days (excluding today)`
        );
        console.log(
            "Prorated price:",
            `$${newTierPrice} - $${proration.unusedCreditRefund.toFixed(2)} - $${proration.serviceFeeRefund.toFixed(2)} = $${resultRounded}`
        );
        console.groupEnd();
        /* eslint-enable no-console */
    }

    return resultRounded;
}
